import { Checkbox, CircularProgress, FormControl, FormControlLabel, InputAdornment, Toolbar, Typography } from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import lodash from 'lodash';
import uuid from 'uuid';
import { firestore } from 'firebase/app';
import TariffHelper, { ISPULine, ISPUTariff } from '../../../@types/model/transformer/tariffs/tariffs';
import TransformerHelper, { ITransformer } from '../../../@types/model/transformer/transformer';
import { ITransformerGroup } from '../../../@types/model/transformer/transformerGroup';
import { ITransformerInvoice, TransformerInvoiceHelper } from '../../../@types/model/transformer/invoice/transformerInvoice';
import TransformerReadingHelper, { ITransformerReading } from '../../../@types/model/transformer/reading/transformerReading';
import { IAuthState, IRootState } from '../../../@types/redux';
import { FirebaseService } from '../../../services/firebaseService';
import generalFunctions from '../../../store/general/functions';
import MasterDataValueAddedTaxFunctions from '../../../store/masterData/valueAddedTax/functions';
import { IValueAddedTax } from '../../../@types/model/masterData/valueAddedTax/valueAddedTax';
import DropdownButton from '../../customComponents/button/DropdownButton';
import TransformerGroupDropdown from '../../customComponents/selector/transformer/GroupSelector';
import TariffConfirmTable from './table/ConfirmTable';
import SPUCapturingTable from './table/SPUCaptureTable';

interface ITransformerSPUCaptureProps {
    auth : IAuthState;
    valueAddedTaxes : Array<IValueAddedTax>;
    onDismiss : () => void;
}

const TransformerSPUCapture = (props : ITransformerSPUCaptureProps) : React.ReactElement => {
    const maxDate: moment.Moment = moment.utc().startOf('month');

    const [selectedAccountMonth, setSelectedAccountMonth] = useState<moment.Moment | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedGroup, setSelectedGroup] = useState<string | undefined>(undefined);
    const [showCapture, setShowCapture] = useState<boolean>(true);
    const [spuTariffs, setSpuTariffs] = useState<Array<ISPUTariff>>([]);
    const [transformers, setTransformers] = useState<Array<ITransformer>>([]);
    const [invoices, setInvoices] = useState<Array<ITransformerInvoice<ISPULine>>>([]);
    const [currentInvoices, setCurrentInvoices] = useState<Array<ITransformerInvoice<ISPULine>>>([]);
    const [isTariffChange, setIsTariffChange] = useState<boolean>(false);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        MasterDataValueAddedTaxFunctions.load();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = async (
        newSelectedAccountMonth : moment.Moment | null,
        newSpuTariffs : Array<ISPUTariff>,
        newTransformers : Array<ITransformer>,
        newSelectedGroup ?: string,
    ) : Promise<void> => {
        if (!newSelectedAccountMonth) return;
        if (!newSelectedGroup) return;
        if (!newSpuTariffs.length) return;

        const currentInvoicesData = (await TransformerInvoiceHelper.collection<ISPULine>()
            .where('type', '==', 'spu')
            .where('monthDate', '==', firestore.Timestamp.fromMillis(newSelectedAccountMonth.valueOf()))
            .get())
            .docs
            .map(x => x.data())
            .filter(x => x.isActive);

        const newInvoices : Array<ITransformerInvoice<ISPULine>> = [];

        try {
            const startOfMonth = moment.utc(newSelectedAccountMonth.valueOf());
            const endOfMonth = moment.utc(newSelectedAccountMonth.valueOf()).endOf('month').startOf('day');
    
            // Gets transformer meter readings for all transformers within the user selected group and date.
            const promises = newTransformers.map(n => TransformerReadingHelper.get(n.EPNumber, startOfMonth.valueOf(), endOfMonth.valueOf()).get());
            const usages = (await Promise.all(promises)).reduce((value, n) => value.concat(n.docs.map(x => x.data())), [] as Array<ITransformerReading>);
    
            newTransformers
                .forEach((transformer) => {
                    // Sums up all meter readings for the transformer within the specified month period.
                    const totalUsage = usages
                        .filter(n => n.Electricalpoint.id === transformer.EPNumber)
                        .reduce((value, reading) => 
                            value + reading.TransformerMeterNumberReadings
                                .filter(x => x.Approved)
                                .reduce((sum, meterReading) => sum + (meterReading.Usage), 0), 0);
                
                    const lines : Array<ISPULine> = [];

                    // Adds invoices that have been already created in for the month.
                    const currentInvoice = currentInvoicesData.find(x => x.epNumber === transformer.EPNumber);

                    if (currentInvoice) {
                        newInvoices.push({
                            ...currentInvoice,
                        });
                        return;
                    }

                    // If there are more than one list of tariffs which happens on a tariff change.
                    // Then create a separate line for each.
                    if (newSpuTariffs.length > 1) {
                        newSpuTariffs.forEach((tariff, index) => {
                            const line = {
                                transformer,
                                tariff,
                            };
    
                            const tariffStartDate = moment.utc(tariff.startDate).startOf('day');
                            const tariffEndDate = !tariff.endDate ? null : moment.utc(tariff.endDate).startOf('day');
    
                            const startDate = tariffStartDate.isAfter(startOfMonth) ? tariffStartDate : startOfMonth;
                            const endDate = tariffEndDate?.isBefore(endOfMonth) ? tariffEndDate : endOfMonth;
    
                            const days = endDate.diff(startDate, 'days') + index;

                            const tariffValues = TariffHelper.getSPUTariffValues(tariff, transformer);

                            lines.push({
                                ...line,
                                guid: uuid.v4(),
                                days,
                                totalUsage,
                                calculatedUsage: totalUsage,
                                tariffValues,
                                total: 0,
                            });
                        });
                    } else {
                        const tariff = newSpuTariffs[0];
                        const days = newSelectedAccountMonth?.daysInMonth() ?? 0;
                        const tariffValues = TariffHelper.getSPUTariffValues(tariff, transformer);

                        lines.push({
                            transformer,
                            guid: uuid.v4(),
                            days,
                            tariff,
                            tariffValues,
                            totalUsage,
                            calculatedUsage: totalUsage,
                            total: 0,
                        });
                    }

                    newInvoices.push(invoiceFromLines(transformer, lines, newSelectedAccountMonth, newSelectedGroup));
                });
        } catch (ex) {
            generalFunctions.generalShowError(ex, `${ex}`);
        } finally {
            setInvoices(newInvoices);
            setCurrentInvoices(currentInvoicesData);
        }
    };

    const loadTariffs = async (selectedMonthDate : moment.Moment, currentTariffChange : boolean) : Promise<void> => {
        setIsLoading(true);

        try {
            const tariffs = (await TariffHelper.spuCollection().get())
                .docs
                .map(n => ({ ...n.data() }))
                .sort((a, b) => a.startDate - b.startDate);

            const filteredSpuTariffs = tariffs
                .filter(x => selectedMonthDate.isBetween(
                    moment.utc(x.startDate),
                    x.endDate ? moment.utc(x.endDate).subtract(1, 'day') : moment.utc(selectedMonthDate).endOf('month'),
                    'month', '[]'))
                .sort((a, b) => a.startDate - b.startDate);

            // Manual force for tariff change, we get the previous tariff
            if (currentTariffChange && filteredSpuTariffs.length === 1) {
                const tariff = filteredSpuTariffs[0];
                const index = tariffs.findIndex(x => x.ref.id === tariff.ref.id);

                if (index > 0) {
                    filteredSpuTariffs.unshift(tariffs[index - 1]);
                }
            }
            
            setSpuTariffs(filteredSpuTariffs);
            // setIsTariffChange(filteredSpuTariffs.length > 1);

            if (filteredSpuTariffs.length < 1) {
                generalFunctions.generalShowErrorSnackbar('No SPU Tariffs Found For This Month.');
            } else {
                await loadData(
                    selectedMonthDate,
                    filteredSpuTariffs,
                    transformers,
                    selectedGroup
                );
            }
        } catch (ex) {
            generalFunctions.generalShowErrorSnackbar(`${ex}`);
        } finally {
            setIsLoading(false);
        }
    };

    const loadTransformers = async (epGroup : string) : Promise<void> => {
        setIsLoading(true);

        try {
            const transformersData = await TransformerHelper.collection().where('EPGroup', '==', epGroup).get();
            const filteredTransformers = transformersData.docs.map(n => n.data()).filter(x => !!x.IsActive);
            setTransformers(filteredTransformers);

            await loadData(
                selectedAccountMonth,
                spuTariffs,
                filteredTransformers,
                epGroup
            );
        } catch (ex) {
            generalFunctions.generalShowError(ex, 'Error loading transformers.');
        } finally {
            setIsLoading(false);
        }
    };

    const save = async () : Promise<void> => {
        const auth = props.auth;

        if (!auth.session) return;

        setIsLoading(true);

        try {
            const currentInvoicesNumbers = currentInvoices.map(x => x.epNumber);
            const invoicesToSave = invoices
                .filter(x => !currentInvoicesNumbers.includes(x.epNumber))
                .filter(x => x.totalBilled) // We only want to create invoices for EPs that are billed.
                .filter(x => x.paymentDueDate) // We only want to create invoices for EPs that have payment due dates
                .map(invoice => ({
                    ...invoice,
                    createdBy: auth.session?.firebaseUser.uid ?? '',
                    createdByEmployee: auth.session?.employee.EmployeeNumber ?? '',
                    createdByName: auth.session?.employee.Name ?? '',
                    createdOn: moment.utc().valueOf(),
                    updatedBy: auth.session?.firebaseUser.uid ?? '',
                    updatedByEmployee: auth.session?.employee.EmployeeNumber ?? '',
                    updatedByName: auth.session?.employee.Name ?? '',
                    updatedOn: moment.utc().valueOf(),
                    group: selectedGroup ?? '',
                    monthDate: selectedAccountMonth?.valueOf() ?? 0,
                }));

            await TransformerInvoiceHelper.saveMany(invoicesToSave);
            generalFunctions.generalShowSuccessSnackbar('Success');
            props.onDismiss();
        } catch (ex) {
            generalFunctions.generalShowError(ex, `${ex}`);
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const invoiceFromLines = (
        transformer : ITransformer,
        lines : Array<ISPULine>,
        newSelectedAccountMonth : moment.Moment | null,
        newSelectedGroup ?: string,
    ) : ITransformerInvoice<ISPULine> => {
        if (!newSelectedAccountMonth) throw new Error('Selecting Month Required');

        const vat = FirebaseService.getVatByMonth(newSelectedAccountMonth, props.valueAddedTaxes);
        const auth = props.auth;

        if (!auth.session) throw new Error('Auth Required');

        return TransformerInvoiceHelper.calculateSPUTotal({
            guid: uuid.v4(),
            transformer,
            accountNumber: transformer.AccountNumber ?? '',
            days: lodash.sumBy(lines, line => line.days),
            epNumber: transformer.EPNumber,
            epNumberRef: TransformerHelper.doc(transformer.EPNumber).path,
            poleNumber: transformer.PoleNumber ?? '',
            lines: [ ...lines ],
            totalUsage: lodash.sumBy(lines, x => x.totalUsage),
            totalInterest: 0, 
            reactiveEnergy: 0,
            utilizedCapacity: 0,
            vatPercentage: vat,
            totalCharge: 0,
            vatCharge: 0,
            totalDue: 0,
            totalBilled: 0,
            type: 'spu',
            paymentDueDate: null,
            demandReading: 0,
            adminCharge: 0,
            serviceCharge: 0,
            standardConnectionCharge: 0,
            createdBy: auth.session.firebaseUser.uid,
            createdByEmployee: auth.session.employee.EmployeeNumber ?? '',
            createdByName: auth.session.employee.Name,
            createdOn: moment.utc().valueOf(),
            updatedBy: auth.session.firebaseUser.uid,
            updatedByEmployee: auth.session.employee.EmployeeNumber ?? '',
            updatedByName: auth.session.employee.Name,
            updatedOn: moment.utc().valueOf(),
            paid: false,
            paidBy: null,
            paidByName: null,
            paidByEmployeeNumber: null,
            paidOn: null,
            journaled: false,
            journalNr: null,
            journal: null,
            journaledBy: null,
            journaledByName: null,
            journaledByEmployeeNumber: null,
            journaledOn: null,
            id: '',
            group: newSelectedGroup ?? '',
            monthDate: newSelectedAccountMonth.valueOf(),
            related: null,
            relatedId: null,
            reverted: false,
            credit: 0,
            isActive: true,
        });
    };

    const onMonthDateChanged = (momentDateValue : moment.Moment | null) : void => {
        const newMonthDate = !momentDateValue ? null : moment.utc(momentDateValue.valueOf()).startOf('month');
        setSpuTariffs([]);
        setSelectedAccountMonth(newMonthDate);

        if (!newMonthDate) return;

        loadTariffs(newMonthDate, isTariffChange);
    };

    const onEPGroupChange = (value ?: ITransformerGroup) : void => {
        const newGroup = value?.name;
        setTransformers([]);
        setSelectedGroup(newGroup);

        if (!newGroup) return;

        loadTransformers(newGroup);
    };

    const onCaptureSubmit = (submittedInvoices : Array<ITransformerInvoice<ISPULine>>) : void => {
        setInvoices(submittedInvoices);
        setShowCapture(false);
    };

    const onConfirmCancel = () : void => {
        setShowCapture(true);
    };

    const onConfirmClick = () : void => {
        save();
    };

    const onConfirmRowClick = (selectedEpNumberValue : string) : void => {
        setShowCapture(true);
    };

    const onTariffChange = (event : React.ChangeEvent<HTMLInputElement>, checked : boolean) : void => {
        setIsTariffChange(checked);
        if (!selectedAccountMonth) return;

        loadTariffs(selectedAccountMonth, checked);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className='fdc flx1 p10 mh0 mw0 bcg0'>
            {
                showCapture &&
                <Toolbar>
                    <span className={'flx1'} />
                    <div className='fdr flx1 aifs jcfs'>
                        <div className={'fdc flx2 aifs p5 mb10 pr20'}>
                            <FormControl fullWidth>
                                <DatePicker value={selectedAccountMonth}
                                    disabled={isLoading}
                                    views={['month']}
                                    maxDate={maxDate}
                                    onChange={onMonthDateChanged}
                                    format='MMM YYYY'
                                    label='Select Reading Month...'
                                    id='monthDate'
                                    className={'TextField'}
                                    margin='normal'
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position='end'>
                                                <DropdownButton />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        </div>
                        <div className={'fdc flx2 aifs p5 mb10 pr20 mt16'}>
                            <TransformerGroupDropdown
                                fullWidth value={selectedGroup}
                                onChange={onEPGroupChange}
                                type={'SPU'}
                                disabled={isLoading}
                                required
                            />
                        </div>
                        <div className={'fdc flx1 aifs p5 mb10 pr20 mt26'}>
                            <div className='fdr aic'>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            value={isTariffChange}
                                            onChange={onTariffChange}
                                            color='primary'
                                            checked={isTariffChange}
                                            disabled={isLoading}
                                        />
                                    }
                                    labelPlacement='start'
                                    label='Tariff change?'
                                />
                            </div>
                        </div>
                    </div>
                </Toolbar>
            }
            {
                isLoading &&
                <div className={'fdc flx1 aic jcc mh0 mw0'}>
                    <CircularProgress />
                </div>
            }
            {
                !isLoading &&
                isTariffChange &&
                <Typography
                    variant={'body1'}
                    className={'mb5 pl10'}
                >
                    *Enter previous tariff readings in the top row, and new tariff readings in the bottom row.
                </Typography>
            }
            {
                !isLoading &&
                showCapture &&
                selectedAccountMonth &&
                selectedGroup &&
                <div className={'fdc flx1 mh0 mw0'}>
                    <SPUCapturingTable
                        multi={spuTariffs.length > 1}
                        invoices={invoices}
                        onSubmit={onCaptureSubmit}
                        disabledTransformers={currentInvoices.map(x => x.epNumber)}
                    />
                </div>
            }
            {
                !isLoading &&
                !showCapture &&
                !!invoices.length &&
                selectedGroup &&
                <div className={'fdc flx1 mh0 mw0'}>
                    <TariffConfirmTable
                        invoices={invoices}
                        group={selectedGroup}
                        onCancelClick={onConfirmCancel}
                        onConfirmClick={onConfirmClick}
                        onRowClick={onConfirmRowClick}
                        disabledTransformers={currentInvoices.map(x => x.epNumber)}
                    />
                </div>
            }
        </div>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        auth: state.auth,
        valueAddedTaxes: state.masterData.valueAddedTax.valueAddedTaxes,
    };
};

export default connect(
    mapStateToProps,
)(TransformerSPUCapture);
